<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  deal: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();

const dealName = computed(() => props.deal?.name || '');

const formattedAmount = computed(() => {
  if (!props.deal?.amount) return '-';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(props.deal.amount);
});

const stageName = computed(() => props.deal?.stage?.name || '-');

const statusBadgeClass = computed(() => {
  const status = props.deal?.status || 'open';
  const classes = {
    open: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    won: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
    lost: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  };
  return (
    classes[status] ||
    'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
  );
});

const statusLabel = computed(() => {
  const status = (props.deal?.status || 'open').toUpperCase();
  return t(`DEALS.STATUS.${status}`);
});
</script>

<template>
  <div
    class="flex items-center justify-between w-full px-6 py-4 border-b border-slate-200 dark:border-slate-800"
  >
    <div class="flex items-center gap-3 min-w-0">
      <h2
        class="text-xl font-medium text-slate-900 dark:text-slate-100 truncate"
      >
        {{ dealName }}
      </h2>
      <span
        class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0"
        :class="statusBadgeClass"
      >
        {{ statusLabel }}
      </span>
    </div>

    <div class="flex items-center gap-6 flex-shrink-0">
      <div class="flex flex-col items-end">
        <span class="text-xs text-slate-500 dark:text-slate-400">
          {{ t('DEALS.FIELDS.AMOUNT') }}
        </span>
        <span
          class="text-base font-semibold text-slate-900 dark:text-slate-100"
        >
          {{ formattedAmount }}
        </span>
      </div>
      <div class="flex flex-col items-end">
        <span class="text-xs text-slate-500 dark:text-slate-400">
          {{ t('DEALS.FIELDS.STAGE') }}
        </span>
        <span class="text-base font-medium text-slate-900 dark:text-slate-100">
          {{ stageName }}
        </span>
      </div>
    </div>
  </div>
</template>
